import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import type { FundingRequestPrivateDto } from '@/api/kubik/credit'
import type { IConsultingFormValues } from '@/components/composed/ConsultingForm/types'
import { createConsultingRequestBody } from '@/components/composed/ConsultingForm/utils'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { FinancingUxFeedbackModal } from '@/components/composed/modals/FinancingUxFeedbackModal/FinancingUxFeedbackModal'
import type { IFinancingUxFeedbackModalProps } from '@/components/composed/modals/FinancingUxFeedbackModal/types'
import { getUser } from '@/logic/auth/reducer'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { FundingBannerCommunicationMethodSelector } from './components/FundingBannerCommunicationMethodSelector'
import { FundingBannerConsultingRegistrationForm } from './components/FundingBannerConsultingRegistrationForm'
import { FundingBannerError } from './components/FundingBannerError'
import { FundingBannerStepForm } from './components/FundingBannerStepForm'
import { FundingBannerSuccess } from './components/FundingBannerSuccess'
import { EFundingBannerConsultingStep } from './constants'
import { messages } from './messages'
import type { TFinancingConsultingState } from './types'

addModalToWellKnown({ FinancingUxFeedbackModal })

const mapState = (state: RootState) => ({
  user: getUser()(state),
})

export const FundingBanner = () => {
  const { user } = useSelector(mapState)
  const { applyFinancingOffer } = useApplyFinancingOffer(EGtmBlock.CONSULTATION)

  const [state, setState] = useState<TFinancingConsultingState>({
    step: EFundingBannerConsultingStep.FORM,
  })

  const dispatch = useDispatch()

  const handleSuccessFormSubmit = (fundingRequestId: number) => {
    setState(previousState => ({ ...previousState, step: EFundingBannerConsultingStep.COMMUNICATION_METHOD, fundingRequestId }))
  }

  const handleErrorFormSubmit = () => {
    setState(previousState => ({ ...previousState, step: EFundingBannerConsultingStep.ERROR }))
  }

  const handleSuccessFinancingOfferFormSending = useCallback(
    (fundingRequests: FundingRequestPrivateDto[]) => {
      const firstFundingRequestId = fundingRequests?.[0]?.id

      if (firstFundingRequestId) {
        handleSuccessFormSubmit(firstFundingRequestId)
      }

      dispatch(
        openModal({
          options: { dialogId: 'FinancingUxFeedbackModal', shouldBlockedScroll: false },
          contentOptions: { isHasSkipButton: false } as IFinancingUxFeedbackModalProps['options'],
        }),
      )
    },
    [dispatch],
  )

  const { setOptions, profile } = useApplyingFinancingOfferForm({
    shouldSkipSuccessModal: true,
    productId: FundingProduct.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
    onSuccess: handleSuccessFinancingOfferFormSending,
    onError: handleErrorFormSubmit,
  })

  const handleOpenLoginDialog = (phoneNumber: string, loanAmount: number) => {
    setState(previousState => {
      if (previousState.step === EFundingBannerConsultingStep.FORM) {
        return { ...previousState, step: EFundingBannerConsultingStep.LOGIN, phoneNumber, loanAmount }
      }

      return previousState
    })
  }

  const handleCloseLoginDialog = (phoneNumber: string) => {
    const values: IConsultingFormValues = {
      phone: phoneNumber,
      loanAmount: '0',
      purpose: messages.comment,
      agreement: true,
    }

    setOptions({
      formValues: createConsultingRequestBody({
        values,
        user,
        productId: FundingProduct.MARKETPLACE,
      }),
    })

    setState(previousState => {
      if (previousState.step === EFundingBannerConsultingStep.LOGIN) {
        return { ...previousState, step: EFundingBannerConsultingStep.FORM, phoneNumber }
      }

      return previousState
    })
  }

  const handleOpenRegistrationForm = (phoneNumber: string, loanAmount: number) => {
    setState(previousState => {
      if (previousState.step === EFundingBannerConsultingStep.FORM) {
        return { ...previousState, step: EFundingBannerConsultingStep.REGISTRATION, phoneNumber, loanAmount }
      }

      return previousState
    })
  }

  const handleSendRequest = async (phoneNumber: string, loanAmount: number) => {
    try {
      const values: IConsultingFormValues = {
        phone: phoneNumber,
        loanAmount: String(loanAmount),
        purpose: null,
        agreement: true,
      }
      const body = createConsultingRequestBody({ values, user, productId: FundingProduct.MARKETPLACE })

      const fundingRequests = await applyFinancingOffer({
        body: {
          ...body,
          comment: messages.comment,
        },
      })
      const firstFundingRequestId = fundingRequests?.[0]?.id

      if (!firstFundingRequestId) {
        throw new Error('There is no funding request id')
      }

      handleSuccessFormSubmit(firstFundingRequestId)
    } catch {
      handleErrorFormSubmit()
    }
  }

  const handleGoBackToForm = () => {
    setState({
      step: EFundingBannerConsultingStep.FORM,
    })
  }

  const handleRegistrationComplete = async (phoneNumber: string, loanAmount: number) => {
    await handleSendRequest(phoneNumber, loanAmount)
  }

  const handleSuccessUpdateFundingRequest = () => {
    setState(previousState => {
      if (previousState.step === EFundingBannerConsultingStep.COMMUNICATION_METHOD) {
        return { ...previousState, step: EFundingBannerConsultingStep.SUCCESS }
      }

      return previousState
    })

    dispatch(
      openModal({
        options: { dialogId: 'FinancingUxFeedbackModal', shouldBlockedScroll: false },
        contentOptions: { isHasSkipButton: false } as IFinancingUxFeedbackModalProps['options'],
      }),
    )
  }

  const handleOpenConsultingForm = () => {
    setState({
      step: EFundingBannerConsultingStep.FORM,
    })
  }

  switch (state.step) {
    case EFundingBannerConsultingStep.FORM:
    case EFundingBannerConsultingStep.LOGIN:
      return (
        <FundingBannerStepForm
          phoneNumber={profile.phone}
          onOpenLoginDialog={handleOpenLoginDialog}
          onCloseLoginDialog={handleCloseLoginDialog}
          onOpenRegistrationForm={handleOpenRegistrationForm}
          onSendRequest={handleSendRequest}
        />
      )
    case EFundingBannerConsultingStep.COMMUNICATION_METHOD: {
      return (
        <FundingBannerCommunicationMethodSelector
          fundingRequestId={state.fundingRequestId}
          onSuccessUpdateFundingRequest={handleSuccessUpdateFundingRequest}
          onErrorUpdateFundingRequest={handleErrorFormSubmit}
        />
      )
    }
    case EFundingBannerConsultingStep.SUCCESS: {
      return <FundingBannerSuccess fundingRequestId={state.fundingRequestId} onResetStep={handleOpenConsultingForm} />
    }
    case EFundingBannerConsultingStep.REGISTRATION: {
      return (
        <FundingBannerConsultingRegistrationForm
          phoneNumber={state.phoneNumber}
          onGoBack={handleGoBackToForm}
          onRegistrationComplete={handleRegistrationComplete}
          loanAmount={state.loanAmount}
        />
      )
    }
    case EFundingBannerConsultingStep.ERROR: {
      return <FundingBannerError onResetStep={handleOpenConsultingForm} />
    }
    default: {
      return <FundingBannerError onResetStep={handleOpenConsultingForm} />
    }
  }
}
